import React from 'react';
import { motion } from 'framer-motion';

export interface MagicBentoProps {
  children: React.ReactNode;
  variant?: 'default' | 'button' | 'card' | 'outline' | 'glass';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement>) => void;
  disabled?: boolean;
  href?: string;
  target?: string;
  type?: 'button' | 'submit' | 'reset'; // Added type prop
  glowEffect?: boolean;
  shimmerEffect?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  role?: string;
  tabIndex?: number;
}

const MagicBento: React.FC<MagicBentoProps> = ({
  children,
  variant = 'default',
  size = 'md',
  className = '',
  onClick,
  disabled = false,
  href,
  target,
  type = 'button', // Default type for buttons
  glowEffect = false,
  shimmerEffect = true,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  role,
  tabIndex,
}) => {
  const baseClasses = 'relative overflow-hidden transition-all duration-300 ease-out';
  
  const variantClasses = {
    default: 'bg-neutral-white/5 backdrop-blur-sm border border-neutral-white/10 rounded-xl',
    button: 'bg-primary-blue hover:bg-primary-blue-dark text-neutral-white rounded-lg font-semibold cursor-pointer shadow-lg hover:shadow-xl hover:shadow-primary-blue/25 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
    card: 'bg-neutral-white/5 backdrop-blur-sm border border-neutral-white/10 rounded-xl hover:border-primary-blue/50 hover:bg-neutral-white/10 cursor-pointer group',
    outline: 'bg-transparent border-2 border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-neutral-white rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
    glass: 'bg-neutral-white/10 backdrop-blur-md border border-neutral-white/20 rounded-xl shadow-lg'
  };

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm min-h-[36px] xs:min-h-[40px]',
    md: 'px-4 py-3 xs:px-6 text-base min-h-[44px]',
    lg: 'px-6 py-4 xs:px-8 text-lg min-h-[48px] xs:min-h-[52px]',
    xl: 'px-8 py-5 xs:px-10 text-xl min-h-[52px] xs:min-h-[56px]'
  };

  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Check if device supports hover (not touch-only)
  const supportsHover = typeof window !== 'undefined' && 
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const motionProps = {
    whileHover: !disabled && !prefersReducedMotion && supportsHover && 
      (variant === 'button' || variant === 'card' || variant === 'outline') ? { 
      scale: variant === 'button' ? 1.02 : 1.01,
      transition: { duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] as const }
    } : undefined,
    whileTap: !disabled && !prefersReducedMotion && (variant === 'button' || variant === 'outline') ? { 
      scale: 0.98,
      transition: { duration: 0.1, ease: [0.42, 0, 0.58, 1] as const }
    } : undefined,
    initial: prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: prefersReducedMotion ? { duration: 0 } : { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const }
  };

  const shimmerElement = shimmerEffect && (variant === 'button' || variant === 'card') && (
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-neutral-white/10 to-transparent -translate-x-full group-hover:translate-x-full hover:translate-x-full transition-transform duration-700 ease-out pointer-events-none" />
  );

  const glowElement = glowEffect && (
    <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-blue via-primary-blue-light to-primary-blue rounded-xl opacity-0 group-hover:opacity-20 hover:opacity-20 transition-opacity duration-300 blur-sm -z-10" />
  );

  if (href) {
    return (
      <motion.a
        href={href}
        target={target}
        className={combinedClasses}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        role={role}
        tabIndex={tabIndex}
        {...motionProps}
      >
        {children}
        {shimmerElement}
        {glowElement}
      </motion.a>
    );
  }

  const Component = (variant === 'button' || variant === 'outline') ? motion.button : motion.div;

  return (
    <Component
      className={combinedClasses}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      role={role}
      tabIndex={tabIndex}
      type={Component === motion.button ? type : undefined} // Apply type only if it's a button
      {...motionProps}
    >
      {children}
      {shimmerElement}
      {glowElement}
    </Component>
  );
};

export default MagicBento;